import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { FormInput } from 'components/form';
import { useModal } from 'hooks/useModal';
import { Box, Button, Stack } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { APIRequestSort, ModalsKeys } from 'global';
import { MUITablePagination } from 'components/shared/MUITable';

import { GlobalDealSheetRulesTable } from './table';
import { DealSheetRulesFilterType } from './types';
import { DealSheetRulesTableFilter } from './filters';
import { dealSheetConfigurationApi } from '../../api';
import { initialDealSheetRulesFiltersValue } from './constants';

export const GlobalRulesTableContainer = () => {
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);

  const { handleOpen: handleOpenDetails } = useModal(ModalsKeys.DealSheetRuleDetailsDialog);

  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState(initialDealSheetRulesFiltersValue);
  const [sorting, setSorting] = useState<APIRequestSort>({ key: 'created_date_time', value: 'desc' });
  const [pagination, setPagination] = useState<Omit<MUITablePagination, 'onChange' | 'total'>>({
    page: 1,
    perPage: 15,
  });

  const { data, previousData, loading } = useQuery(dealSheetConfigurationApi.getTableGlobalConfig, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        search,
        sorting,
        pagination,
        ...(search ? { search } : {}),
        filters: {
          ...filters,
          [DealSheetRulesFilterType.STATE]: filters.STATE.map(({ value }) => value),
          [DealSheetRulesFilterType.COUNTRY]: filters.COUNTRY.map(({ value }) => value),
          [DealSheetRulesFilterType.PROVINCE]: filters.PROVINCE.map(({ value }) => value),
          [DealSheetRulesFilterType.PAYMENT_TYPE]: filters.PAYMENT_TYPE.map(({ value }) => value),
          [DealSheetRulesFilterType.CUSTOMER_TYPE]: filters.CUSTOMER_TYPE.map(({ value }) => value),
          [DealSheetRulesFilterType.EQUIPMENT_TYPE]: filters.EQUIPMENT_TYPE.map(({ value }) => value),
        },
      },
    },
  });

  const tableData = data?.dealSheets?.result?.items ?? previousData?.dealSheets?.result?.items ?? [];

  return (
    <Stack gap="0.5rem" height="calc(100% - 10rem)">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap="0.5rem" minWidth="50%">
          <FormInput
            value={search}
            variant="outlined"
            label="Search by name or description"
            onChange={value => setSearch(value)}
          />

          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setIsFiltersOpen(prevState => !prevState)}
          >
            Filters
          </Button>
        </Stack>
      </Stack>

      <Box height="calc(100% - 3rem)">
        <GlobalDealSheetRulesTable
          isLoading={loading}
          tableData={tableData}
          sorting={{ ...sorting, onChange: setSorting }}
          actions={{
            onViewDetails: id =>
              handleOpenDetails({ configId: id, document: dealSheetConfigurationApi.getGlobalConfig }),
          }}
          pagination={{
            ...pagination,
            total: data?.dealSheets?.result?.pagination?.total ?? 0,
            onChange: nextValue => setPagination(prev => ({ ...prev, ...nextValue })),
          }}
        />
      </Box>

      <DealSheetRulesTableFilter open={isFiltersOpen} onClose={() => setIsFiltersOpen(false)} onChanged={setFilters} />
    </Stack>
  );
};
